import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../../../../hook/useAppDispatch';
import { useAppSelector } from '../../../../../../hook/useAppSelector';
import { useAutocompleteSearch } from '../../../../../../hook/autocomplete-search/useAutocompleteSearch';
import { useClearAutocompleteSearch } from '../../../../../../hook/autocomplete-search/useClearAutocompleteSearch';
import {
  setDisableFooterSum,
  setShouldExpandSum,
  setShouldViewPerCurrencyFalse,
} from '../../../../../../store/slice/stats-slice';

import {
  setSearchInput,
  clearAutocompleteList,
  getDeviceList,
} from '../../../../../../store/slice/health-monitor/device-list';
import { setSearch } from '../../../../../../store/slice/search-slice';

import magnifier from '../../../../../../assets/img/png/health-monitor/magnifier.png';
import AutocompleteDropdown from './AutocompleteDropdown';
import { SearchExecutionMode } from '../../../../../../enum/SearchExecutionMode';

interface Props {
  shouldAutocompleteSearch?: boolean;
  mode: SearchExecutionMode;
}

const SearchInput: FC<Props> = ({ shouldAutocompleteSearch = false, mode }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const deviceSearchInput = useAppSelector((state) => state.deviceList.searchInput);
  const liveSearchInput = useAppSelector((state) => state.searchSlice.input);

  const shouldRefreshDeviceListOnClear = useRef(false);

  useAutocompleteSearch(shouldAutocompleteSearch);
  useClearAutocompleteSearch(shouldAutocompleteSearch);

  // Reset device input on mount
  useEffect(() => {
    if (mode === SearchExecutionMode.MANUAL) dispatch(setSearchInput(''));
  }, [dispatch, mode]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (mode === SearchExecutionMode.MANUAL) {
      dispatch(setSearchInput(value));

      if (shouldRefreshDeviceListOnClear.current) {
        shouldRefreshDeviceListOnClearHandler(false);
        dispatch(getDeviceList());
      }
    } else {
      // live search mode
      dispatch(setSearch(value));
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mode === SearchExecutionMode.MANUAL && e.key === 'Enter') {
      shouldRefreshDeviceListOnClearHandler(true);
      dispatch(clearAutocompleteList());
      dispatch(getDeviceList());
    }
  };

  const shouldRefreshDeviceListOnClearHandler = (shouldRefresh: boolean) => {
    shouldRefreshDeviceListOnClear.current = shouldRefresh;
  };

  const inputValue = mode === SearchExecutionMode.MANUAL ? deviceSearchInput : liveSearchInput;

  useEffect(() => {
    if (inputValue === '') dispatch(setDisableFooterSum(false));
  }, [inputValue, dispatch]);

  useEffect(() => {
    if (inputValue && inputValue.length > 0) {
      dispatch(setShouldViewPerCurrencyFalse());
      dispatch(setShouldExpandSum(false));
      dispatch(setDisableFooterSum(true));
    }
  }, [dispatch, inputValue]);

  return (
    <div className="search_box">
      <img src={magnifier} alt="magnifier" />
      <input
        type="text"
        spellCheck={false}
        name="searchInput"
        value={inputValue || ''}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        placeholder={t('healthMonitor.header.filter.inputPlaceholder')}
      />
      {inputValue && (
        <span
          className="clear_search"
          onClick={() => dispatch(mode === SearchExecutionMode.MANUAL ? setSearchInput('') : setSearch(''))}
        >
          &times;
        </span>
      )}

      {shouldAutocompleteSearch && mode === SearchExecutionMode.MANUAL && (
        <AutocompleteDropdown handleShouldRefreshDeviceListOnClear={shouldRefreshDeviceListOnClearHandler} />
      )}
    </div>
  );
};

export default SearchInput;
